/**
 * Centralized Field Configuration
 * 
 * This file defines all field configurations, behaviors, data sources, and validation rules
 * in one place. All components should use this configuration to ensure consistency.
 * 
 * DEPENDENT FIELDS FEATURE:
 * Fields can specify dependencies using the 'dependentFields' property. When a field with
 * dependencies is added to a workflow stage without its dependent fields, a warning icon
/**
 * Centralized Field Configuration
 * 
 * This file defines all field configurations, behaviors, data sources, and validation rules
 * in one place. All components should use this configuration to ensure consistency.
 * 
 * DEPENDENT FIELDS FEATURE:
 * Fields can specify dependencies using the 'dependentFields' property. When a field with
 * dependencies is added to a workflow stage without its dependent fields, a warning icon
 * will be displayed next to the field in the settings page.
 *
 * Example:
 * 'subcategory_id': {
 *   label: 'Subcategory',
 *   type: 'select',
 *   dependentFields: ['category_id'], // This field depends on category_id
 *   // ... other config
 * }
 * 
 * When 'subcategory_id' is added to a stage without 'category_id', users will see
 * an orange exclamation icon with a tooltip explaining the missing dependencies.
 * 
 * CUSTOM FIELDS FEATURE:
 * Custom fields are defined in the database and fetched dynamically.
 * They are merged into the field configuration system with keys like `custom_field_{id}`.
 * Supported types: text, textarea, select, yesno
 * Custom field values are stored in `item_custom_field_values` table, not the `items` table.
 * Components should use `useFieldConfigs` to get the merged configuration.
 */

import React from 'react';
import { selectableTaskOrders } from '../utils/taskOrderUtils';
import { formatItemId, formatItemRef } from '../utils/itemIdFormat';

// Field configuration factory that takes data dependencies as parameters
export const createFieldConfig = ({
  activeUsers = [],
  endorserUsers = [],
  openItems = [], // Changed from endorsedItems
  categories = [],
  subcategories = [],
  workflowStages = [],
  projects = [],
  detectabilityChoices = [],
  originatingDisciplineChoices = [],
  rootCauseCategoryChoices = [],
  opportunityThreatChoices = [],
  typeChoices = [],
  riskTypeChoices = [],
  assignedDisciplineChoices = [],
  currentCategoryId = null,
  currentProjectId = null,
  onCategoryChange = null,
  riskMatrixConfig = null,
  currentItemId = null // Add current item ID to filter it out
} = {}) => {

  // Create dynamic dependent fields for risk zone based on risk matrix configuration
  const createRiskZoneDependencies = () => {
    const dependencies = ['custom_field_23']; // Always include probability (field 23)
    
    // Add threat score fields from risk matrix configuration if available
    if (riskMatrixConfig?.threat_score_fields && Array.isArray(riskMatrixConfig.threat_score_fields)) {
      riskMatrixConfig.threat_score_fields.forEach(fieldId => {
        const customFieldKey = `custom_field_${fieldId}`;
        if (!dependencies.includes(customFieldKey)) {
          dependencies.push(customFieldKey);
        }
      });
    } else {
      // Fallback to legacy threat score fields if no configuration
      dependencies.push('custom_field_21', 'custom_field_22'); // cost_impact, schedule_impact
    }
    
    return dependencies;
  };

  const fieldConfigs = {
    // Basic text fields
    'title': {
      label: 'Title',
      type: 'text',
      tooltip: 'Provide a brief, clear name that identifies the specific idea or issue.',
      required: true,
      validation: {
        maxLength: 255,
        minLength: 1
      }
    },

    'description': {
      label: 'Description',
      type: 'textarea',
      tooltip: 'Use the ISO31000 structure; If something happens, there is a risk that, which may result in these consequences',
      validation: {
        maxLength: 2000
      }
    },

    'status': {
      label: 'Status',
      type: 'text'
    },

    'assigned_discipline': {
      label: 'Assigned Discipline',
      type: 'select',
      options: assignedDisciplineChoices.map(choice => ({
        value: choice.assigned_discipline,
        label: choice.assigned_discipline
      })),
      isClearable: true,
      dataSource: 'assignedDisciplineChoices'
    },

    'initial_notes': {
      label: 'Initial Notes',
      type: 'textarea',
      tooltip: 'Add any preliminary comments, context, or observations relevant to the initial identification of this idea or issue.'
    },

    'current_assessment_notes': {
      label: 'Current Assessment Notes',
      type: 'textarea'
    },

    'endorsement_notes': {
      label: 'Endorsement Notes',
      type: 'textarea'
    },

    'close_comment': {
      label: 'Close Notes',
      type: 'readonly',
      tooltip: 'Notes captured when the item was closed. Recorded automatically and cannot be edited.',
      canBeRequired: false,
      description: 'Notes entered in the close modal when the item was closed'
    },

    'root_cause_title': {
      label: 'Root Cause Title',
      type: 'text',
      validation: {
        maxLength: 255
      }
    },

    'root_cause_analysis': {
      label: 'Justification for Root Cause Analysis',
      type: 'textarea',
      validation: {
        maxLength: 2000
      }
    },

    'justification': {
      label: 'Justification',
      type: 'textarea',
      validation: {
        maxLength: 2000
      }
    },

    'management_comments': {
      label: 'Management Comments',
      type: 'textarea',
      validation: {
        maxLength: 2000
      }
    },

    'immediate_actions_taken': {
      label: 'Immediate Actions Taken',
      type: 'textarea',
      validation: {
        maxLength: 2000
      }
    },

    'is_root_cause_analysis_required': {
      label: 'Is Root Cause Analysis Required',
      type: 'yesno',
      tooltip: 'Check this box if a formal investigation into the underlying causes of the risk is necessary.',
      options: [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' }
      ],
      isClearable: false,
      defaultValue: 'No'
    },

    'no_action_items_required': {
      label: 'No Action Items Required',
      type: 'yesno',
      tooltip: 'Check this box if there are no action items that need to be created.',
      options: [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' }
      ],
      isClearable: false,
      defaultValue: 'No'
    },

    'exportable': {
      label: 'Exportable',
      type: 'yesno',
      options: [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' }
      ],
      isClearable: false,
      defaultValue: 'No'
    },

    // User selection fields
    'owner': {
      label: 'Owner',
      type: 'select',
      tooltip: 'Assign the person who will be responsible for monitoring and managing the idea or issue',
      options: activeUsers.map(user => ({
        value: user.email,
        label: user.name
      })),
      isClearable: true,
      dataSource: 'activeUsers'
    },

    'primary_owner': {
      label: 'Primary Owner',
      type: 'select',
      options: activeUsers.map(user => ({
        value: user.email,
        label: user.name
      })),
      isClearable: true,
      dataSource: 'activeUsers'
    },

    'secondary_owner': {
      label: 'Secondary Owner',
      type: 'select',
      options: activeUsers.map(user => ({
        value: user.email,
        label: user.name
      })),
      isClearable: true,
      dataSource: 'activeUsers',
      dependentFields: ['primary_owner'] // Secondary usually comes after primary
    },

    'endorser': {
      label: 'Endorser',
      type: 'select',
      options: endorserUsers.map(user => ({
        value: user.email,
        label: user.name
      })),
      isClearable: true,
      dataSource: 'endorserUsers'
    },

    // Category fields with cascading logic
    'category_id': {
      label: 'Category',
      type: 'select',
      tooltip: 'Choose the appropriate category for this idea or issue',
      options: categories.map(cat => ({
        value: cat.CategoryID || cat.id,
        label: cat.CategoryName || cat.categoryname
      })),
      isClearable: true,
      dataSource: 'categories',
      onChange: onCategoryChange,
      rules: {
        cascadesClear: ['subcategory_id'] // When this field changes, clear these fields
      }
    },

    'subcategory_id': {
      label: 'Subcategory',
      type: 'select',
      options: subcategories.map(subcat => ({
        value: subcat.SubcategoryID || subcat.id,
        label: subcat.SubcategoryName || subcat.subcategoryname
      })),
      isClearable: true,
      dataSource: 'subcategories',
      dependencies: ['category_id'], // This field depends on category_id
      dependentFields: ['category_id'], // Fields that this field depends on
      isDisabled: !currentCategoryId, // Disabled when no category selected
      rules: {
        requiresParent: 'category_id' // Cannot have value without parent field
      }
    },

    'project_id': {
      label: 'Task Order',
      type: 'select',
      options: selectableTaskOrders(projects, currentProjectId).map(project => ({
        value: project.id,
        label: project.name
      })),
      isClearable: true,
      dataSource: 'projects',
      canBeReadonly: true
    },

    // Single-select fields
    'child_of': {
      label: 'Child Of',
      tooltip: 'Link to the parent that this item is a systemic issue of. Any open or closed item can be selected; closed ones are marked.',
      type: 'select',
      options: openItems
        .filter(item => {
          // Filter out current item if we have additionalData with current item info
          const currentItemId = this?.currentItemId;
          return currentItemId ? item.id !== currentItemId : true;
        })
        // Open first, then newest - the item being linked is usually recent and open.
        .sort((a, b) => {
          const aClosed = a?.status === 'closed';
          const bClosed = b?.status === 'closed';
          if (aClosed !== bClosed) return aClosed ? 1 : -1;
          return (Number(b?.id) || 0) - (Number(a?.id) || 0);
        })
        .map(item => {
          const isClosed = item?.status === 'closed';
          return {
            value: item.id,
            // "(Closed)" is part of the label so it shows on the collapsed
            // control and so typing "closed" filters to those items.
            label: `${formatItemRef(item.id)} - ${item.title}${isClosed ? ' (Closed)' : ''}`,
            description: item.description || null,
            owner: item.owner || null,
            isClosed,
            item: item // Store full item data for custom rendering
          };
        }),
      isClearable: true,
      isMulti: false,
      dataSource: 'openItems',
      formatOptionLabel: (option, { context }) => {
        if (context === 'menu') {
          // Use the stored item data for more reliable access to properties
          const item = option.item || {};
          const title = item.title || option.label || `Item ${option.value}`;
          const description = item.description || option.description;
          const owner = item.owner || option.owner;
          const isClosed = option.isClosed ?? item.status === 'closed';
          
          return (
            <div style={{ padding: '0' }}>
              <div style={{ 
                fontWeight: '500', 
                color: isClosed ? '#6c757d' : 'var(--nsi-brand)', 
                marginBottom: '4px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>{formatItemRef(option.value)} - {title}</span>
                {isClosed && (
                  <span style={{
                    flexShrink: 0,
                    padding: '1px 7px',
                    borderRadius: '10px',
                    background: '#f1f3f5',
                    border: '1px solid #ced4da',
                    color: '#6c757d',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px'
                  }}>
                    Closed
                  </span>
                )}
              </div>
              {owner && (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#666', 
                  marginBottom: '2px'
                }}>
                  Owner: {owner}
                </div>
              )}
              {description && (
                <div style={{ 
                  fontSize: '11px', 
                  color: '#888',
                  lineHeight: '1.3'
                }}>
                  Description: {description.length > 100 ? `${description.substring(0, 100)}...` : description}
                </div>
              )}
            </div>
          );
        }
        const isClosed = option.isClosed ?? option.item?.status === 'closed';
        return `${formatItemRef(option.value)} - ${option.item?.title || 'No title'}${isClosed ? ' (Closed)' : ''}`;
      },
      customComponents: {
        // Custom rendering for single select display
        renderDisplayValue: (value, navigate) => {
          if (!value) return '-';
          return (
            <span 
              className="item-id-pill"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/items/${value}`);
              }}
              title={`Go to item ${formatItemId(value)}`}
              style={{ 
                color: '#0066cc', 
                cursor: 'pointer',
                textDecoration: 'underline' 
              }}
            >
              ID: {formatItemId(value)}
            </span>
          );
        }
      }
    },

    // Readonly fields
    'created_on': {
      label: 'Created On',
      type: 'readonly',
      formatter: (value) => value ? new Date(value).toLocaleDateString() : '-'
    },

    'created_by': {
      label: 'Created By',
      type: 'readonly'
    },

    'updated_at': {
      label: 'Last Updated',
      type: 'readonly',
      formatter: (value) => value ? new Date(value).toLocaleDateString() : '-'
    },

    'current_stage': {
      label: 'Current Stage',
      type: 'readonly',
      formatter: (value) => {
        if (!value) return '-';
        const stage = workflowStages.find(s => s.id === value);
        return stage?.stage_name || value;
      }
    },

    'brainstorming_id': {
      label: 'Brainstorming ID',
      type: 'readonly'
    },

    // Choice-based select fields
    'detectability': {
      label: 'Velocity',
      type: 'select',
      tooltip: 'Specify the speed at which the risk would impact the project once the event occurs.',
      options: detectabilityChoices.map(choice => ({
        value: choice.detectability,
        label: choice.detectability
      })),
      isClearable: true,
      dataSource: 'detectabilityChoices'
    },

    'originating_discipline': {
      label: 'Originating Discipline',
      type: 'select',
      tooltip: 'Select the department or functional group that first identified this idea or issue',
      options: originatingDisciplineChoices.map(choice => ({
        value: choice.originating_discipline,
        label: choice.originating_discipline
      })),
      isClearable: true,
      dataSource: 'originatingDisciplineChoices'
    },

    'root_cause_category': {
      label: 'Root Cause Category',
      type: 'select',
      options: rootCauseCategoryChoices.map(choice => ({
        value: choice.root_cause_category,
        label: choice.root_cause_category
      })),
      isClearable: true,
      dataSource: 'rootCauseCategoryChoices'
    },

    'opportunity_or_threat': {
      label: 'Opportunity or Threat',
      type: 'select',
      options: opportunityThreatChoices.map(choice => ({
        value: choice.opportunity_threat,
        label: choice.opportunity_threat
      })),
      isClearable: true,
      dataSource: 'opportunityThreatChoices'
    },

    'type': {
      label: 'Idea/Issue Type',
      type: 'select',
      options: typeChoices.map(choice => ({
        value: choice.type,
        label: choice.type
      })),
      isClearable: true,
      dataSource: 'typeChoices'
    },

    'risk_type': {
      label: 'Risk Type',
      type: 'select',
      options: riskTypeChoices.map(choice => ({
        value: choice.risk_type,
        label: choice.risk_type
      })),
      isClearable: true,
      dataSource: 'riskTypeChoices'
    },

    // Risk zone field (calculated and stored in database)
    'risk_zone': {
      label: 'Risk Zone',
      type: 'readonly',
      tooltip: 'This read-only field displays the calculated risk level numerically (0-25) based on the matrix of probability and impact (cost and schedule).',
      dependentFields: createRiskZoneDependencies(), // Dynamic dependencies based on risk matrix configuration
      formatter: (value, riskMatrixConfig = null) => {
        if (!value || isNaN(value)) return '-';
        
        const score = parseInt(value);
        
        // If we have the risk matrix config with zone thresholds, use them
        if (riskMatrixConfig?.zone_thresholds) {
          for (const [zoneName, threshold] of Object.entries(riskMatrixConfig.zone_thresholds)) {
            if (threshold && threshold.min !== undefined && threshold.max !== undefined &&
                score >= threshold.min && score <= threshold.max) {
              return `${zoneName} (Score: ${score})`;
            }
          }
        }
        
        // Fallback to hardcoded thresholds (extended to handle up to 36)
        let zone;
        if (score >= 1 && score <= 5) {
          zone = 'Low';
        } else if (score >= 6 && score <= 12) {
          zone = 'Medium';
        } else if (score >= 13 && score <= 19) {
          zone = 'High';
        } else if (score >= 20 && score <= 36) {
          zone = 'Very High';
        } else {
          // Handle any score outside expected range
          zone = 'Unknown';
        }
        
        return `${zone} (Score: ${score})`;
      },
      description: 'Risk classification zone calculated from Probability and threat score fields'
    },

    // Component fields
    'tasks': {
      label: 'Tasks',
      type: 'component',
      component: 'TasksDisplay',
      description: 'Displays all tasks for this item and optionally tasks from child items',
      props: {
        // The component will receive the current item ID as parentId
        // and can fetch its own child tasks plus tasks from child items
        includeChildTasks: true // Enable child task rollup by default
      },
      validation: {
        // Custom validation for component fields when marked as required
        // This validation should be called from the component with tasks data
        validateRequired(allTasks, itemId, allItems = []) {
          if (!allTasks) {
            return { isValid: false, message: 'Unable to fetch tasks for validation' };
          }
          
          // Get direct tasks for this item
          const directTasks = allTasks.filter(task => task.parent_id === itemId);
          
          // Get child items if any
          const childItems = allItems.filter(item => {
            const childOfValue = item.child_of;
            return childOfValue === parseInt(itemId);
          });
          
          // Get tasks from child items
          const childTaskIds = childItems.map(child => child.id);
          const childTasks = allTasks.filter(task => childTaskIds.includes(task.parent_id));
          
          const allRelatedTasks = [...directTasks, ...childTasks];
          
          // If no tasks exist, consider it valid (empty is complete)
          if (allRelatedTasks.length === 0) {
            return { isValid: true, message: 'No tasks to complete' };
          }
          
          // Check if all tasks are completed or canceled
          const incompleteTasks = allRelatedTasks.filter(
            task => task.status !== 'Completed' && task.status !== 'Canceled'
          );
          
          if (incompleteTasks.length > 0) {
            const directIncomplete = incompleteTasks.filter(task => directTasks.find(dt => dt.id === task.id));
            const childIncomplete = incompleteTasks.filter(task => childTasks.find(ct => ct.id === task.id));
            
            let message = `${incompleteTasks.length} task(s) still need to be completed`;
            if (directIncomplete.length > 0 && childIncomplete.length > 0) {
              message += ` (${directIncomplete.length} direct, ${childIncomplete.length} from child items)`;
            } else if (childIncomplete.length > 0) {
              message += ` (${childIncomplete.length} from child items)`;
            }
            
            return { 
              isValid: false, 
              message,
              details: {
                totalTasks: allRelatedTasks.length,
                completedTasks: allRelatedTasks.length - incompleteTasks.length,
                incompleteTasks: incompleteTasks.map(t => ({
                  id: t.id,
                  description: t.description,
                  status: t.status,
                  isFromChild: !directTasks.find(dt => dt.id === t.id)
                }))
              }
            };
          }
          
          return { 
            isValid: true, 
            message: `All tasks complete`,
            details: {
              totalTasks: allRelatedTasks.length,
              completedTasks: allRelatedTasks.length,
              directTasks: directTasks.length,
              childTasks: childTasks.length
            }
          };
        }
      }
    },

    // Lifecycle component
    'lifecycle': {
      label: 'Item Lifecycle',
      type: 'component',
      component: 'Lifecycle',
      description: 'Displays the stage transition history and timeline for this item',
      props: {
        // The component will receive the current item with stage_history
        // and workflowStages for reference
      },
      readOnly: true, // This component is display-only
      canBeRequired: false, // Never allow this to be set as required
      validation: {
        // No validation needed for display-only component
        validateRequired() {
          return { isValid: true, message: 'Lifecycle display is informational only' };
        }
      }
    },

    // Attachments component
    'attachments': {
      label: 'Attachments',
      type: 'component',
      component: 'AttachmentsUpload',
      description: 'Upload attachments and documents for this item',
      props: {
        // The component will receive the current item ID for upload
      },
      canBeRequired: false, // Component fields cannot be required
      canBeReadonly: false, // Component fields cannot be readonly
      validation: {
        // No validation needed for attachment component
        validateRequired() {
          return { isValid: true, message: 'Attachments component is optional' };
        }
      }
    },

    // Custom Fields component
    'custom_fields': {
      label: 'Custom Fields',
      type: 'component',
      component: 'CustomFieldsRenderer',
      description: 'Displays and allows editing of custom fields for this item',
      props: {
        // The component will receive itemId, projectId, editable, etc.
      },
      canBeRequired: false, // Component fields cannot be required
      canBeReadonly: false, // Component fields cannot be readonly
      validation: {
        validateRequired() {
          return { isValid: true, message: 'Custom fields component is informational' };
        }
      }
    }
  };

  return fieldConfigs;
};

// Helper function to get field configuration
export const getFieldConfig = (fieldId, dependencies = {}) => {
  const configs = createFieldConfig(dependencies);
  return configs[fieldId] || { 
    label: fieldId, 
    type: 'text',
    isCustom: true // Flag to indicate this is a fallback config
  };
};

// Helper function to get dependency information for a field
export const getFieldDependencies = (fieldId, fieldConfigs) => {
  const fieldConfig = fieldConfigs[fieldId];
  if (!fieldConfig || !fieldConfig.dependentFields) {
    return [];
  }
  
  return fieldConfig.dependentFields.map(dependentFieldId => ({
    id: dependentFieldId,
    label: fieldConfigs[dependentFieldId]?.label || dependentFieldId
  }));
};

// Helper function to check for missing dependent fields
export const checkFieldDependencies = (fieldId, assignedFields, fieldConfigs) => {
  const fieldConfig = fieldConfigs[fieldId];
  
  if (!fieldConfig || !fieldConfig.dependentFields) {
    return { hasMissingDependencies: false, missingFields: [] };
  }
  
  const assignedFieldIds = assignedFields.map(field => field.fieldId || field.id);
  const missingFields = fieldConfig.dependentFields.filter(
    dependentFieldId => !assignedFieldIds.includes(dependentFieldId)
  );
  
  return {
    hasMissingDependencies: missingFields.length > 0,
    missingFields: missingFields.map(fieldId => ({
      id: fieldId,
      label: fieldConfigs[fieldId]?.label || fieldId
    }))
  };
};

// Helper function to validate field value against its configuration
// Note: Custom field validation is supported through the standard validation rules
export const validateFieldValue = (fieldId, value, config) => {
  if (!config.validation) return { isValid: true };

  const errors = [];
  const validation = config.validation;

  // Required validation
  if (config.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
    errors.push(`${config.label} is required`);
  }

  // String validations
  if (typeof value === 'string') {
    if (validation.maxLength && value.length > validation.maxLength) {
      errors.push(`${config.label} must be ${validation.maxLength} characters or less`);
    }
    if (validation.minLength && value.length < validation.minLength) {
      errors.push(`${config.label} must be at least ${validation.minLength} characters`);
    }
  }

  // Number validations
  if (config.type === 'number' && value !== null && value !== undefined && value !== '') {
    const numValue = Number(value);
    if (isNaN(numValue)) {
      errors.push(`${config.label} must be a valid number`);
    } else {
      if (validation.min !== undefined && numValue < validation.min) {
        errors.push(`${config.label} must be ${validation.min} or greater`);
      }
      if (validation.max !== undefined && numValue > validation.max) {
        errors.push(`${config.label} must be ${validation.max} or less`);
      }
    }
  }

  // Dependency validations
  if (config.rules?.requiresParent) {
    // This would need access to other field values - implement in component
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Helper function to format display values
// Note: Custom fields use standard types (text, textarea, select, yesno) and are handled automatically
export const formatDisplayValue = (fieldId, value, config, additionalData = {}) => {
  // A yesno is one of two states, never blank. An absent value means the
  // field has not been touched yet, which is the default, not "unknown".
  if (config.type === 'yesno') {
    const resolved = (value === 'Yes' || value === true || value === 'true')
      ? 'Yes'
      : (value === 'No' || value === false || value === 'false')
        ? 'No'
        : (config.defaultValue || 'No');
    const option = config.options?.find(opt => opt.value === resolved);
    return option ? option.label : resolved;
  }

  if (!value && value !== 0 && !Array.isArray(value)) return '-';

  // Custom formatter
  if (config.formatter) {
    return config.formatter(value, additionalData);
  }

  // Custom component renderer
  if (config.customComponents?.renderDisplayValue) {
    return config.customComponents.renderDisplayValue(value, additionalData.navigate);
  }

  // Default formatting by type
  switch (config.type) {
    case 'select':
      if (!value) return '-';
      const selectedOption = config.options?.find(opt => {
        // Handle type coercion for numeric IDs
        if (typeof opt.value === 'number' && typeof value === 'string') {
          return opt.value === parseInt(value);
        }
        if (typeof opt.value === 'string' && typeof value === 'number') {
          return parseInt(opt.value) === value;
        }
        return opt.value === value;
      });
      return selectedOption ? selectedOption.label : value;

    case 'multiselect':
      if (!Array.isArray(value) || value.length === 0) return '-';
      const selectedOptions = config.options?.filter(opt => value.includes(opt.value)) || [];
      if (selectedOptions.length === 0) return value.join(', ');
      return selectedOptions.map(opt => opt.label).join(', ');

    case 'calculated':
      if (config.calculation && config.calculation.formula && additionalData.item) {
        const calculatedValue = config.calculation.formula(additionalData.item, additionalData.choicesData);
        if (config.calculation.formatter) {
          return config.calculation.formatter(calculatedValue);
        }
        return calculatedValue;
      }
      return '-';

    case 'component':
      // Component fields need special handling - return a React component marker
      // The actual rendering will be handled in the UI layer
      return { 
        __isComponent: true, 
        component: config.component,
        props: {
          ...config.props,
          parentId: additionalData.item?.id,
          parentTitle: additionalData.item?.title
        }
      };

    case 'readonly':
    case 'text':
    case 'textarea':
    case 'number':
    default:
      return value;
  }
};

// Export field types for reference
export const FIELD_TYPES = {
  TEXT: 'text',
  TEXTAREA: 'textarea',
  SELECT: 'select',
  MULTISELECT: 'multiselect',
  YESNO: 'yesno',
  NUMBER: 'number',
  READONLY: 'readonly',
  CALCULATED: 'calculated',
  COMPONENT: 'component'
};

// Export data sources for reference
export const DATA_SOURCES = {
  ACTIVE_USERS: 'activeUsers',
  OPEN_ITEMS: 'openItems', // Changed from ENDORSED_ITEMS
  CATEGORIES: 'categories',
  SUBCATEGORIES: 'subcategories',
  WORKFLOW_STAGES: 'workflowStages',
  PROJECTS: 'projects',
  DETECTABILITY_CHOICES: 'detectabilityChoices',
  ORIGINATING_DISCIPLINE_CHOICES: 'originatingDisciplineChoices',
  ROOT_CAUSE_CATEGORY_CHOICES: 'rootCauseCategoryChoices',
  OPPORTUNITY_THREAT_CHOICES: 'opportunityThreatChoices',
  TYPE_CHOICES: 'typeChoices',
  RISK_TYPE_CHOICES: 'riskTypeChoices',
  ASSIGNED_DISCIPLINE_CHOICES: 'assignedDisciplineChoices',
  CUSTOM_FIELDS: 'customFields'
};